// ============================================
// APP.JS — Autenticación JWT + Router + Launcher
// ============================================
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard';
import Login from './Login';
import ExpoAlianza from './ExpoAlianza';
import Launcher from './Launcher';
import ExpoKiosko from './ExpoKiosko';
import Campanas from './Campanas';
import VistaPublicaProducto from './VistaPublicaProducto';
import GarantiaPublica from './GarantiaPublica';
import DatosPublico from './DatosPublico';
import VerificarPublico from './VerificarPublico';
import PortalExterno from './PortalExterno';
import InvitacionExterno from './InvitacionExterno';
import AvisoPrivacidad from './AvisoPrivacidad';
import PaginaProducto from './PaginaProducto';
import AvisoNuevaVersion from './components/AvisoNuevaVersion';
import { UsuarioProvider, useUsuario } from './context/UsuarioContext';
import { TemaProvider } from './utils/tema';

import { STORAGE_KEYS, storage } from './utils/storage';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const PP = "'Poppins', sans-serif";

function SplashScreen() {
  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh', background: '#f3f4f4',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: PP,
    }}>
      <img src="/Logo Original Cuadrado.png" alt="BeepMX" style={{
        width: 100, height: 100, animation: 'beepPulse 1.5s ease-in-out infinite',
      }} />
      <div style={{ fontSize: 14, color: '#5e666e', marginTop: 12 }}>Cargando...</div>
      <style>{`@keyframes beepPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(0.96); } }`}</style>
    </div>
  );
}

// AVISO-VERSION (2026-09-18): el aviso de version nueva se monta arriba del todo, para que salga
// en cualquier pantalla —tambien en el login y en Expo—: quien esta a punto de entrar tambien
// tiene que enterarse de que su copia esta vieja.
function AppContent() {
  const { usuario: ctxUsuario, setUsuario: setCtxUsuario, cerrarSesion } = useUsuario();
  const [cargando, setCargando] = useState(true);
  const [autenticado, setAutenticado] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const init = async () => {
      const token = storage.getRaw(STORAGE_KEYS.AUTH_TOKEN);
      const appGuardada = storage.getRaw(STORAGE_KEYS.CURRENT_APP);

      if (!token) {
        setCargando(false);
        return;
      }

      try {
        const res = await fetch(`${API}/api/auth/verificar`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const u = data.usuario;
          const mapped = {
            id: u.id, correo: u.correo, nombre: u.nombre,
            rol: mapRol(u.rol), area: mapArea(u.rol),
            empresa: u.empresa, esExterno: u.es_externo,
            portal: u.portal || 'beep',
            esDueno: u.es_dueno === true,   // ENCAPSULAR-TUEXPO: solo el dueño ve /expo (lo dice el servidor)
            permisos_extra: [], permisos_quitados: [],
          };
          setCtxUsuario(mapped);
          setAutenticado(true);

          // Ir a la última app usada si estamos en una ruta genérica
          const path = location.pathname;
          if (path === '/' || path === '/beep-login' || path === '/expo-login') {
            // ENCAPSULAR-TUEXPO: solo el dueño vuelve a /expo; a cualquier otro se le olvida esa app guardada.
            if (appGuardada === 'expo' && mapped.esDueno) {
              navigate('/expo', { replace: true });
            } else if (appGuardada === 'expo') {
              storage.set(STORAGE_KEYS.CURRENT_APP, 'beep');
            }
            // Si appGuardada es 'beep' o no hay, quedarse en / (Dashboard)
          }
          setCargando(false);
          return;
        }
      } catch (e) {
        console.error('Error verificando token:', e);
      }

      // Fallback con datos guardados
      const u = storage.get(STORAGE_KEYS.USER_DATA);
      if (u) {
        try {
          const mapped = {
            id: u.id, correo: u.correo, nombre: u.nombre,
            rol: mapRol(u.rol || u.rol_db), area: mapArea(u.rol || u.rol_db),
            empresa: u.empresa, esExterno: u.esExterno,
            esDueno: u.esDueno === true,
            permisos_extra: [], permisos_quitados: [],
          };
          setCtxUsuario(mapped);
          setAutenticado(true);

          const path = location.pathname;
          if (path === '/' && appGuardada === 'expo' && mapped.esDueno) {
            navigate('/expo', { replace: true });
          }
          setCargando(false);
          return;
        } catch {}
      }

      // Token inválido, limpiar
      storage.remove(STORAGE_KEYS.AUTH_TOKEN);
      storage.remove(STORAGE_KEYS.USER_DATA);
      storage.remove(STORAGE_KEYS.CURRENT_APP);
      setCargando(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (usuarioData) => {
    const mapped = {
      id: usuarioData.id, correo: usuarioData.correo, nombre: usuarioData.nombre,
      rol: mapRol(usuarioData.rol), area: mapArea(usuarioData.rol),
      empresa: usuarioData.empresa, esExterno: usuarioData.esExterno,
      portal: usuarioData.portal || 'beep',   // 'contratista' = portal del externo (lo dice el servidor)
      esDueno: usuarioData.esDueno === true,  // ENCAPSULAR-TUEXPO
      permisos_extra: [], permisos_quitados: [],
    };
    setCtxUsuario(mapped);
    setAutenticado(true);

    // CONTRATISTAS-EXTERNOS Bloque 2: la cuenta externa solo conoce su portal.
    if (mapped.portal === 'contratista') { navigate('/contratista', { replace: true }); return; }

    // Si estamos en el flujo de expo, ir a expo (solo el dueño: para el resto /expo no existe)
    if ((location.pathname === '/expo' || location.pathname === '/expo-login') && mapped.esDueno) {
      storage.set(STORAGE_KEYS.CURRENT_APP, 'expo');
      navigate('/expo', { replace: true });
    } else {
      storage.set(STORAGE_KEYS.CURRENT_APP, 'beep');
      navigate('/', { replace: true });
    }
  };

  const handleLogout = () => {
    storage.clearBeepKeys();
    cerrarSesion();
    setAutenticado(false);
    navigate('/', { replace: true });
  };

  if (cargando) return <SplashScreen />;

  const u = ctxUsuario;
  // REFACTOR-STORAGE: app activa actual desde storage. Se usa como key del
  // Dashboard para forzar remount cuando se vuelve de Expo (fix bug PWA:
  // el Dashboard mantenia state obsoleto al regresar). El Dashboard guarda
  // 'beep' como CURRENT_APP en su useEffect, asi que al cambiar de 'expo'
  // a 'beep' la key cambia y React desmonta+monta -> recarga favoritos, etc.
  const appActiva = storage.getRaw(STORAGE_KEYS.CURRENT_APP) || 'beep';

  return (
    <>
    <AvisoNuevaVersion />
    <Routes>
      {/* Launcher — si no hay sesión muestra selector (o va directo al login si solo hay una app), si hay sesión muestra Dashboard */}
      <Route path="/" element={
        autenticado && u && u.portal === 'contratista'
          ? <Navigate to="/contratista" replace />
          : autenticado && u
          ? <Dashboard
              key={appActiva}
              usuario={u.correo}
              nombreUsuario={u.nombre}
              rolUsuario={u.rol}
              usuarioObj={u}
              setLogueado={(v) => { if (!v) handleLogout(); }}
              onIrAExpo={() => { storage.set(STORAGE_KEYS.CURRENT_APP, 'expo'); navigate('/expo'); }}
            />
          : <Launcher />   /* SELECTOR-LIMPIO: con UNA app visible entra directo a su login; con dos o mas, enseña el selector */
      } />

      {/* Login Beep OS */}
      <Route path="/beep-login" element={
        autenticado && u
          ? <Navigate to="/" replace />
          : <Login onLogin={handleLogin} modo="beep" />
      } />

      {/* TuExpo — ENCAPSULAR-TUEXPO (2026-10-15): solo la cuenta marcada como dueño (lo dice el servidor) ve la app.
          Para cualquier otra sesion, y sin sesion, /expo se comporta como una ruta que no existe (vuelve a /).
          El login rapido de Expo (Login modo="expo") queda sin usar a proposito: no se borra, por si se reabre. */}
      <Route path="/expo" element={
        autenticado && u && u.esDueno
          ? <ExpoAlianza
              usuario={{ id: u.id, nombre: u.nombre, rol: u.rol, empresa: u.empresa }}
              onLogout={handleLogout}
              onSalir={() => {
                storage.set(STORAGE_KEYS.CURRENT_APP, 'beep');
                storage.remove(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB);
                navigate('/');
              }}
              modoAplicativo={true}
            />
          : <Navigate to="/" replace />
      } />

      {/* CONTRATISTAS-EXTERNOS Bloque 2: portal del contratista (solo su rol) e invitacion (enlace nominal de un solo uso) */}
      <Route path="/contratista" element={
        autenticado && u && u.portal === 'contratista'
          ? <PortalExterno usuario={u} onLogout={handleLogout} />
          : <Navigate to="/" replace />
      } />
      <Route path="/contratista/invitacion/:token" element={<InvitacionExterno onLogin={handleLogin} />} />

      {/* Expo Kiosko — pantalla pública para tableta, sin login */}
      <Route path="/expo-kiosko" element={<ExpoKiosko />} />

      {/* Campañas — privado, requiere auth */}
      <Route path="/campanas" element={
        autenticado && u
          ? <Campanas />
          : <Navigate to="/" replace />
      } />

      {/* Página de producto — privada (vendedor), full-page detalle */}
      <Route path="/tienda/producto/:id_unico" element={
        autenticado && u
          ? <PaginaProducto />
          : <Navigate to="/" replace />
      } />
      {/* Compatibilidad legacy con la ruta vieja /producto/:id_unico */}
      <Route path="/producto/:id_unico" element={
        autenticado && u
          ? <PaginaProducto />
          : <Navigate to="/" replace />
      } />

      {/* Catch-all: si autenticado ir a Dashboard, si no al Launcher */}
      <Route path="*" element={
        autenticado && u
          ? <Navigate to="/" replace />
          : <Navigate to="/" replace />
      } />
    </Routes>
    </>
  );
}

// Map DB roles to Dashboard-expected role names
function mapRol(dbRol) {
  const map = {
    direccion: 'Dirección',
    administracion: 'Administrador',
    supervisor: 'Supervisor',
    tecnico: 'Técnico',
    comercial: 'Comercial',
    marketing: 'Marketing',
    almacen: 'Almacén',
  };
  return map[dbRol] || dbRol || 'Técnico';
}

function mapArea(dbRol) {
  const map = {
    direccion: 'Dirección',
    administracion: 'Administración',
    supervisor: 'Operación',
    tecnico: 'Operación',
    comercial: 'Comercial',
    marketing: 'Marketing',
    almacen: 'Almacén',
  };
  return map[dbRol] || 'Operación';
}

// Wrapper que decide entre rutas públicas (sin auth) y la app autenticada.
// Las rutas /p/:codigo y /v/:codigo se renderizan SIN el contexto de Usuario,
// para que funcionen aunque el cliente no tenga cuenta.
function RouterRoot() {
  return (
    <Routes>
      <Route path="/p/:codigo" element={<VistaPublicaProducto />} />
      <Route path="/v/:codigo" element={<VistaPublicaProducto />} />
      {/* GARANTIAS CAPA 2: pagina publica del certificado (QR). Solo lectura, sin login. */}
      <Route path="/garantia/:codigo" element={<GarantiaPublica />} />
      {/* PORTAL DE AUTOSERVICIO DEL CLIENTE (2026-09-08): link temporal, sin login. */}
      <Route path="/datos/:token" element={<DatosPublico />} />
      {/* VERIFICADOR de documentos oficiales (carta de datos bancarios). v2: formulario folio + codigo;
          la ruta con codigo se conserva para las ligas viejas y solo muestra el mensaje generico. */}
      <Route path="/verificar" element={<VerificarPublico />} />
      <Route path="/verificar/:codigo" element={<VerificarPublico />} />
      {/* AVISO DE PRIVACIDAD (2026-10-14): publico y estatico, lo exige Meta para publicar la app de WhatsApp. */}
      <Route path="/privacidad" element={<AvisoPrivacidad />} />
      <Route path="*" element={
        <UsuarioProvider>
          <AppContent />
        </UsuarioProvider>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <TemaProvider>
        <RouterRoot />
      </TemaProvider>
    </BrowserRouter>
  );
}

export default App;
